import { Query, QueryKey } from '@tanstack/react-query';
import produce, { Draft } from 'immer';
import { queryClient } from 'src/query-client';
import { PageDto } from '@api/Api';

export function isListQuery(query: Query) {
  return (
    query.state.data !== undefined &&
    'pages' in (query.state.data as any) &&
    Array.isArray((query.state.data as any).pages) &&
    ((query.state.data as any).pages as any[]).every(
      (page: any) => 'edges' in page && Array.isArray(page.edges)
    )
  );
}

type Page<T> = PageDto & {
  edges: { node: T; cursor: string }[];
  startCursor: string | null;
  endCursor: string | null;
};

export function getItemPlaceholderFromListQuery<T extends { id: string }>(
  queryKey: QueryKey,
  id: string
): T | undefined {
  const queriesData = queryClient
    .getQueriesData({
      queryKey,
      predicate: isListQuery
    })
    .sort((a, b) => {
      const aQuery = queryClient.getQueryState(a[0]);
      const bQuery = queryClient.getQueryState(b[0]);
      if (!aQuery || !bQuery) return 0;
      return bQuery.dataUpdatedAt - aQuery.dataUpdatedAt;
    });
  for (const [_, data] of queriesData) {
    const pages = (data as any).pages as Page<T>[];
    for (const page of pages) {
      const item = page.edges.find((edge) => edge.node.id === id);
      if (item) return item.node;
    }
  }
  return undefined;
}

export function optimisticListUpdate<T extends { id: string }>(
  filters: { queryKey: QueryKey; predicate?: (query: Query) => boolean },
  updater: (
    draft: Draft<{
      pages: Page<T>[];
      pageParams: unknown[];
    }>
  ) => void
) {
  return queryClient.setQueriesData(
    {
      queryKey: filters.queryKey,
      predicate: (query) =>
        isListQuery(query) && (!filters.predicate || filters.predicate(query))
    },
    (data: any) =>
      produce(
        data as {
          pages: Page<T>[];
          pageParams: unknown[];
        },
        updater
      )
  );
}

export function optimisticListItemUpdate<T extends { id: string }>(
  filters: {
    queryKey: QueryKey;
    id: string;
    predicate?: (query: Query) => boolean;
  },
  updater: (draft: Draft<T>) => void
) {
  optimisticListUpdate<T>(filters, (draft) => {
    draft.pages.forEach((page) => {
      page.edges.forEach(({ node }) => {
        if (node.id === filters.id) updater(node);
      });
    });
  });
}

export function optimisticListItemRemove<T extends { id: string }>(filters: {
  queryKey: QueryKey;
  id: string;
  predicate?: (query: Query) => boolean;
}) {
  optimisticListUpdate<T>(filters, (draft) => {
    draft.pages.forEach((page) => {
      page.edges = page.edges.filter(({ node }) => node.id !== filters.id);
    });
  });
}

export function optimisticListItemReorder<T extends { id: string }>(
  filters: {
    queryKey: QueryKey;
    id: string;
    predicate?: (query: Query) => boolean;
  },
  prevIndex: number,
  newIndex: number
) {
  if (prevIndex === newIndex) return;
  let beforeId: string | undefined | false = false;
  optimisticListUpdate<T>(filters, (draft) => {
    const newList = draft.pages.flatMap((page) => page.edges);
    const index = newList.findIndex(({ node }) => node.id === filters.id);
    if (index !== prevIndex) return;
    const movedItem = newList.splice(prevIndex, 1)[0];
    newList.splice(newIndex, 0, movedItem);
    beforeId = newList[newIndex - 1]?.node.id;
    draft.pages = [
      {
        edges: newList,
        hasNext: false,
        startCursor: newList[0]?.cursor ?? null,
        endCursor: newList[newList.length - 1]?.cursor ?? null
      }
    ];
    draft.pageParams = [undefined];
  });
  return beforeId as string | undefined | false;
}

export function getPlaceholderQuery<T>(
  queryKey: QueryKey,
  predicate: (data: unknown) => data is T
): T | undefined {
  const queriesData = queryClient
    .getQueriesData({
      queryKey,
      predicate: (query) => predicate(query.state.data)
    })
    .sort((a, b) => {
      const aQuery = queryClient.getQueryState(a[0]);
      const bQuery = queryClient.getQueryState(b[0]);
      if (!aQuery || !bQuery) return 0;
      return bQuery.dataUpdatedAt - aQuery.dataUpdatedAt;
    });
  if (queriesData.length === 0) return undefined;
  const latestQueryData = queriesData[0][1];
  return latestQueryData as T;
}

export function getPlaceholderArrayQuery<T>(
  queryKey: QueryKey
): T[] | undefined {
  return getPlaceholderQuery<T[]>(queryKey, (data): data is T[] =>
    Array.isArray(data)
  );
}

type ArrayLens<TData, TItem> = {
  get: (data: TData) => TItem[];
  set: (data: TData, items: TItem[]) => TData;
};

const identityArrayLens: ArrayLens<any, any> = {
  get: (data) => data,
  set: (_data, items) => items
};

export function optimisticArrayItemAdd<TItem, TData = TItem[]>(
  filters: {
    queryKey: QueryKey;
    predicate?: (query: Query) => boolean;
    lens?: ArrayLens<TData, TItem>;
  },
  newItem: TItem
) {
  const lens = (filters.lens ?? identityArrayLens) as ArrayLens<TData, TItem>;
  return queryClient.setQueriesData(filters, (data: TData | undefined) => {
    if (data === undefined) {
      if (filters.lens) return data;
      return [newItem] as unknown as TData;
    }
    return lens.set(data, [newItem, ...lens.get(data)]);
  });
}

export function optimisticArrayItemRemove<
  TItem extends { id: string },
  TData = TItem[]
>(filters: {
  queryKey: QueryKey;
  id: string;
  predicate?: (query: Query) => boolean;
  lens?: ArrayLens<TData, TItem>;
}) {
  const lens = (filters.lens ?? identityArrayLens) as ArrayLens<TData, TItem>;
  return queryClient.setQueriesData(filters, (data: TData | undefined) => {
    if (data === undefined) return data;
    return lens.set(
      data,
      lens.get(data).filter((item) => item.id !== filters.id)
    );
  });
}

export function optimisticArrayItemUpdate<
  TItem extends { id: string },
  TData = TItem[]
>(
  filters: {
    queryKey: QueryKey;
    id: string;
    predicate?: (query: Query) => boolean;
    lens?: ArrayLens<TData, TItem>;
  },
  updater: (item: TItem) => TItem
) {
  const lens = (filters.lens ?? identityArrayLens) as ArrayLens<TData, TItem>;
  return queryClient.setQueriesData(filters, (data: TData | undefined) => {
    if (data === undefined) return data;
    return lens.set(
      data,
      lens
        .get(data)
        .map((item) => (item.id === filters.id ? updater(item) : item))
    );
  });
}
